import { consume } from '@lit/context';
import { localized, t } from '@weavedev/lit-i18next';
import {
  css,
  html,
  LitElement,
  nothing,
  PropertyValues,
  TemplateResult,
  unsafeCSS,
} from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { createRef, Ref, ref } from 'lit/directives/ref.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

import { ProductionSpeedOptionDto, ProductionSpeedTypeEnum } from '@/api-sdk';
import CheckUrl from '@/assets/svg/check.svg?raw';
import { FocusManagementMixin } from '@/components/configurator/focus-management-step-mixin';
import { iconCircleInfo, iconStopwatch } from '@/components/icons';
import {
  ConfiguratorContextType,
  configuratorGlobal,
  global,
  GlobalComponentType,
} from '@/context';
import { BREAKPOINT_LARGE_PX, BREAKPOINT_MEDIUM_PX } from '@/settings';
import { baseStyles, rootVariablesStyles, typographyStyles } from '@/styles';
import { GTMEventType, LayoutType } from '@/types';
import {
  afterCutoffTime,
  findFirstFocusableRecursive,
  formatDate,
  formatTimeString,
  sendGTMEvent,
  svgUriEncoder,
} from '@/utils';

@customElement('fg-production-speed')
@localized()
export class FgProductionSpeed extends FocusManagementMixin(LitElement) {
  private productionSpeedFieldsetRef: Ref<HTMLFieldSetElement> = createRef();
  inputRefs: Ref<HTMLInputElement>[] = [];

  /**
   * Array of possible production options in the configurator
   */
  @property({ type: Array })
  options!: Array<ProductionSpeedOptionDto>;

  @property({ type: Boolean })
  showProductionSpeedStep = false;

  @property({ type: Boolean })
  loading = false;

  @state()
  optimisticSelectedOption?: string;

  @state()
  selectedProductionSpeedInfo?: { cutoffTime: string; timeInDays: number };

  @state()
  selectedCode?: string | null = null;

  @state()
  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  @consume({ context: configuratorGlobal.context })
  configuratorContext!: ConfiguratorContextType;

  renderInWorkingDays(count: number | null | undefined, layout: LayoutType) {
    if (typeof count !== 'number') {
      return nothing;
    }

    const inWorkingDaysTranslations = {
      retail: 'content.productionSpeed.deliveryInWorkingDays',
      wholesale: 'content.productionSpeed.shippingInWorkingDays',
    };

    return html`
      <span class="production-speed-working-days">
        ${t(inWorkingDaysTranslations[layout], {
          count: count,
        })}
      </span>
    `;
  }

  renderExpectedDate(date: string | null | undefined, layout: LayoutType) {
    const expectedDateTranslation = {
        retail: 'content.configurator.expectedDeliveryDate',
        wholesale: 'content.configurator.expectedShippingDate',
      },
      { locale } = this.globalContext;

    return date
      ? html`
          <div class="expected-date">
            ${t(expectedDateTranslation[layout])}
            <strong>
              ${formatDate(
                date,
                locale,
                layout === 'retail' ? 'long' : 'short',
              )}
            </strong>
          </div>
        `
      : nothing;
  }

  handleProductionSpeedSelect(queryString?: string | null) {
    if (!queryString) {
      return;
    }

    this.dispatchEvent(
      new CustomEvent('option-selected', {
        composed: true,
        detail: {
          option: {
            queryString,
          },
        },
      }),
    );
  }

  optimisticSelectOption(targetCode: string) {
    this.optimisticSelectedOption = targetCode;
  }

  protected willUpdate(_changedProperties: PropertyValues): void {
    if (_changedProperties.has('options')) {
      const { layout } = this.globalContext;
      this.optimisticSelectedOption = undefined;

      const selectedValue = this.options.findIndex(option => option.selected);
      const cutoffTime = this.options[selectedValue]?.cutoffTime;
      const timeInDays =
        layout === 'retail'
          ? this.options[selectedValue]?.deliveryTimeInDays
          : this.options[selectedValue]?.shippingTimeInDays;

      if (
        this.options[selectedValue]?.code &&
        this.selectedCode !== this.options[selectedValue]?.code
      ) {
        const { code } = this.options[selectedValue];

        this.selectedCode = code;

        sendGTMEvent(GTMEventType.PRODUCT_CONFIGURATOR, {
          configurator_step_name: 'select_delivery',
          configurator_step: '5',
          interaction_input: code,
        });
      }

      if (!cutoffTime || !timeInDays) {
        this.selectedProductionSpeedInfo = undefined;
        return;
      }

      this.selectedProductionSpeedInfo = {
        cutoffTime: cutoffTime,
        timeInDays: timeInDays,
      };
    }

    if (
      _changedProperties.has('selectedCode') &&
      _changedProperties.get('selectedCode') === null
    ) {
      this.showGoToNextStepButton = true;
    }

    super.willUpdate(_changedProperties);
  }

  renderCutoffTimeInfo(): TemplateResult | typeof nothing {
    if (
      !this.selectedProductionSpeedInfo?.cutoffTime &&
      typeof this.selectedProductionSpeedInfo?.timeInDays !== 'number'
    ) {
      return nothing;
    }
    const cutoffDay = {
        today: 'content.productionSpeed.cutoffInfo',
        tomorrow: 'content.productionSpeed.cutoffInfoTomorrow',
        todayWholesale: 'content.productionSpeed.cutoffInfoWholesale',
        tomorrowWholesale:
          'content.productionSpeed.cutoffInfoWholesaleTomorrow',
      },
      { layout, locale } = this.globalContext;

    return html`
      <div class="cutoff-time-info">
        ${iconCircleInfo}
        <span>
          ${unsafeHTML(
            t(
              cutoffDay[
                afterCutoffTime(this.selectedProductionSpeedInfo?.cutoffTime)
                  ? layout === 'retail'
                    ? 'tomorrow'
                    : 'tomorrowWholesale'
                  : layout === 'retail'
                    ? 'today'
                    : 'todayWholesale'
              ],
              {
                count: this.selectedProductionSpeedInfo.timeInDays,
                time: formatTimeString(
                  this.selectedProductionSpeedInfo.cutoffTime,
                  locale,
                ),
              },
            ),
          )}
        </span>
      </div>
    `;
  }

  focusFirstOptionElement() {
    this.updateComplete.then(async () => {
      if (this.productionSpeedFieldsetRef?.value === undefined) return;
      const focusableElement = await findFirstFocusableRecursive(
        this.productionSpeedFieldsetRef?.value,
      );
      focusableElement?.focus();
    });
  }

  render() {
    const { layout } = this.globalContext;

    const sortedOptions = [...this.options].sort((a, b) => {
      if (a.code === ProductionSpeedTypeEnum.hsp) return -1;
      if (b.code === ProductionSpeedTypeEnum.hsp) return 1;
      return 0;
    });

    return html`
      <fieldset
        class="production-speed-wrapper"
        ${ref(this.productionSpeedFieldsetRef)}
      >
        ${this.renderNextStepButton(t('headers.chooseDeliverySpeed') ?? '')}
        <legend class="sr-only">${t('headers.chooseDeliverySpeed')}</legend>
        ${sortedOptions.map((option, index) => {
          this.inputRefs[index] = createRef();

          return html`
            <label
              for=${option.code}
              class=${classMap({
                discount: option.discount !== null,
                [option.code]: option.code,
                selected:
                  (option.selected &&
                    (this.optimisticSelectedOption === option.code ||
                      this.optimisticSelectedOption === undefined)) ||
                  this.optimisticSelectedOption === option.code,
                disabled: !option.enabled || this.loading,
              })}
            >
              <input
                ${ref(this.inputRefs[index])}
                ?checked=${(option.selected &&
                  (this.optimisticSelectedOption === option.code ||
                    this.optimisticSelectedOption === undefined)) ||
                this.optimisticSelectedOption === option.code}
                id=${option.code}
                ?disabled=${!option.enabled || this.loading}
                name="production-speed"
                type="radio"
                @change=${() => {
                  if (option.enabled && !this.loading) {
                    this.optimisticSelectOption(option.code);
                    this.handleProductionSpeedSelect(option.queryString);
                  }
                }}
              />
              <div>
                <strong class="production-type">
                  ${option.code === ProductionSpeedTypeEnum.hsp
                    ? html`
                        <span>
                          ${iconStopwatch}
                          ${layout === 'retail'
                            ? t('content.productionSpeed.quickDelivery')
                            : t('content.productionSpeed.24hProduction')}
                        </span>
                      `
                    : layout === 'retail'
                      ? t('content.productionSpeed.standardDelivery')
                      : t('content.productionSpeed.standardProduction')}
                </strong>

                ${option.enabled
                  ? html`
                      ${this.renderInWorkingDays(
                        layout === 'retail'
                          ? option.deliveryTimeInDays
                          : option.shippingTimeInDays,
                        layout,
                      )}
                      ${this.showProductionSpeedStep
                        ? this.renderExpectedDate(
                            layout === 'retail'
                              ? option.expectedDeliveryDate
                              : option.expectedShippingDate,
                            layout,
                          )
                        : nothing}
                    `
                  : nothing}
                ${option.disabledReason && !option.enabled
                  ? html`
                      <span>${option.disabledReason}</span>
                    `
                  : nothing}
              </div>
              ${option.discount && option.enabled
                ? html`
                    <div class="discount-wrapper">
                      <strong>- ${option.discount}</strong>
                      <span>${t('content.discount')}</span>
                    </div>
                  `
                : nothing}
            </label>
          `;
        })}
      </fieldset>
      ${this.renderCutoffTimeInfo()}
    `;
  }

  static styles = [
    baseStyles,
    typographyStyles,
    rootVariablesStyles,
    css`
      .production-speed-wrapper {
        display: flex;
        gap: 0.375rem;
        border: none;
        padding: 0;

        @media screen and (max-width: ${BREAKPOINT_LARGE_PX - 1}px) {
          flex-direction: column;
        }

        @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
          gap: 0.625rem;
        }

        & label {
          display: grid;
          grid-template-columns: max-content auto;
          column-gap: 0.5rem;
          border-radius: var(
            --fg-borderradius,
            var(--fg-fallback-borderradius)
          );
          padding: 1rem;
          background-color: #ffffff;
          cursor: pointer;
          position: relative;
          overflow: hidden;

          @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
            flex-basis: 50%;
          }

          > div {
            display: flex;
            flex-direction: column;
          }

          &.discount {
            grid-template-columns: max-content auto max-content;

            & .discount-wrapper {
              display: flex;
              flex-direction: column;
              align-items: flex-end;
              color: var(
                --fg-message-success,
                var(--fg-fallback-message-success)
              );
            }

            & .discount-wrapper strong {
              font-size: 1.125rem;
            }

            & .discount-wrapper span {
              font-size: 0.75rem;
            }
          }

          &.selected {
            background-color: var(
              --fg-config-selected--light,
              var(--fg-fallback-config-selected--light)
            );
          }

          &.disabled,
          &.disabled input[type='radio'] {
            cursor: default;
          }

          &.disabled::after {
            content: '';
            position: absolute;
            inset: 0;
            background-color: rgba(255, 255, 255, 0.5);
          }
        }
      }
      .production-speed-wrapper:has(.production-speed-working-days) {
        strong.production-type {
          margin-bottom: 4px;
        }
      }

      .production-speed-wrapper:has(.expected-date) {
        .production-speed-working-days {
          margin-bottom: 1rem;
        }
      }

      strong.production-type {
        font-size: 1rem;
        & span {
          line-height: 1.25rem;
          display: flex;
          align-items: center;
          gap: 4px;
        }
      }

      .production-speed-working-days {
        display: block;
      }

      .production-speed-working-days + span {
        display: flex;
        flex-direction: column;
        margin-top: auto;
      }

      .expected-date {
        display: flex;
        flex-direction: column;
      }

      input[type='radio'] {
        appearance: none;
        margin: 0;
        padding: 0;
        border-radius: 50%;
        cursor: pointer;
      }

      :host([part='wholesale']) {
        & .production-speed-wrapper label {
          border-radius: var(
            --fg-borderradius--sm,
            var(--fg-fallback-borderradius--sm)
          );
          background-color: var(--fg-gray--100, var(--fg-fallback-gray--100));

          & .discount-wrapper strong {
            font-size: 1.375rem;
            color: var(--fg-success--dark, var(--fg-fallback-success--dark));
          }

          &.selected {
            background-color: var(--fg-success--light, var(--fg-success--dark));
            border: 1px solid
              var(--fg-success--dark, var(--fg-fallback-success--dark));
          }

          & strong.production-type,
          & strong.production-type span {
            font-weight: 600;
            font-size: 1rem;
            line-height: 1.5rem;
          }

          input[type='radio'] {
            border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray-300));
            background-color: #ffffff;
            width: 24px;
            height: 24px;
            &[checked] {
              border-color: var(--fg-success, var(--fg-fallback-success));
              background-color: var(--fg-success, var(--fg-fallback-success));
              background-image: url(${unsafeCSS(svgUriEncoder(CheckUrl))});
              background-repeat: no-repeat;
              background-position: center;
              background-size: 16px 16px;
            }
          }

          .production-speed-working-days,
          .production-speed-working-days + span {
            font-size: 0.875rem;
          }
        }

        .cutoff-time-info svg {
          color: var(--fg-secondary, var(--fg-fallback-secondary));
        }
      }

      :host([part='retail']) {
        .production-speed-wrapper label {
          border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));

          &.selected {
            border: 2px solid
              var(--fg-config-selected, var(--fg-fallback-config-selected));
          }
        }

        .production-speed-wrapper label:hover:not(.selected):not(.disabled) {
          border-color: var(--fg-primary, var(--fg-fallback-primary));
          box-shadow: var(--fg-boxshadow, var(--fg-fallback-boxshadow));
        }

        input[type='radio'] {
          border: 2px solid var(--fg-gray--500, var(--fg-fallback-gray--500));
          width: 20px;
          height: 20px;

          &[checked] {
            border: 6px solid
              var(--fg-config-selected, var(--fg-fallback-config-selected));
          }
        }
      }

      .cutoff-time-info {
        display: flex;
        margin-top: 0.625rem;
        gap: 0.25rem;

        & svg {
          display: block;
          width: 16px;
          height: 16px;
          flex-shrink: 0;
          color: var(--fg-primary, var(--fg-fallback-primary));
          margin-top: 3px;
        }
      }
    `,
  ];
}
